import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Divider,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import axios from 'axios';

interface ModalGenerarPagoProps {
  isOpen: boolean;
  onClose: () => void;
  predio: any;
  onOpenPagoManual: (concepto: string, monto: number) => void;
}

interface CalculoPago {
  ok: boolean;
  datos: string;
  clave: string;
  fechaUMA: string;
  uma: {
    date: string;
    umaDiaria: number;
    umaMensual: number;
    umaAnual: number;
  };
  resultado: {
    base: number;
    tasa: number;
    minimoUma: number;
    minimoMx: number;
    total: number;
  };
}

const ModalGenerarPago: React.FC<ModalGenerarPagoProps> = ({
  isOpen,
  onClose,
  predio,
  onOpenPagoManual,
}) => {
  const [calculoPago, setCalculoPago] = useState<CalculoPago | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const calcularPago = async () => {
    if (!predio?.ClaveCatastral?.claveCatastralEstandar) {
      setError('No se encontró la clave catastral del predio');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        'https://totolac-backend.togui.com.mx/api/pagos-predial/calcular/por-clave',
        {
          clave: predio.ClaveCatastral.claveCatastralEstandar,
        }
      );

      console.log('Respuesta de la API:', response.data);
      setCalculoPago(response.data);
    } catch (error: any) {
      console.error('Error al calcular el pago:', error);
      setError(
        error.response?.data?.message || 
        'Error al calcular el monto del pago'
      );
      toast({
        title: 'Error',
        description: 'No se pudo calcular el monto del pago',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalOpen = () => {
    if (isOpen && !calculoPago && !isLoading) {
      calcularPago();
    }
  };

  React.useEffect(() => {
    handleModalOpen();
  }, [isOpen]);

  const handleClose = () => {
    setCalculoPago(null);
    setError(null);
    onClose();
  };

  const generarPago = () => {
    if (calculoPago && calculoPago.ok) {
      onOpenPagoManual(calculoPago.datos, calculoPago.resultado.total);
      handleClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          bg="#5b182b"
          color="white"
          textAlign="center"
          fontWeight="bold"
        >
          Generar Pago de Predio
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={6}>
          <VStack spacing={4} align="stretch">
            {/* Información del Predio */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="#5b182b" mb={3}>
                Información del Predio
              </Text>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Clave Catastral:</Text>
                  <Text>{predio?.ClaveCatastral?.claveCatastralEstandar || 'N/A'}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Propietario:</Text>
                  <Text>
                    {predio?.ClaveCatastral?.PropietarioPredio?.[0]?.Propietario
                      ? `${predio.ClaveCatastral.PropietarioPredio[0].Propietario.nombre} ${predio.ClaveCatastral.PropietarioPredio[0].Propietario.primerApellido} ${predio.ClaveCatastral.PropietarioPredio[0].Propietario.segundoApellido || ''}`
                      : 'N/A'}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Dirección:</Text>
                  <Text>{`${predio?.calle || ''} ${predio?.numeroExterior || ''}`}</Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Cálculo del Pago */}
            <Box>
              {/* <Text fontSize="lg" fontWeight="bold" color="#5b182b" mb={3}>
                Cálculo del Pago
              </Text> */}
              
              {isLoading && (
                <Box textAlign="center" py={4}>
                  <Spinner size="lg" color="#5b182b" />
                  <Text mt={2}>Calculando monto...</Text>
                </Box>
              )}

              {error && (
                <Alert status="error">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              {calculoPago && calculoPago.ok && (
                <VStack align="stretch" spacing={3}>
                  {/* <HStack justify="space-between">
                    <Text>Base:</Text>
                    <Text fontWeight="medium">
                      {calculoPago.resultado.base.toFixed(4)}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>Tasa:</Text>
                    <Text fontWeight="medium">
                      {(calculoPago.resultado.tasa * 100).toFixed(2)}%
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>Mínimo UMA:</Text>
                    <Text fontWeight="medium">
                      {calculoPago.resultado.minimoUma} UMAs
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>Mínimo MX:</Text>
                    <Text fontWeight="medium">
                      ${calculoPago.resultado.minimoMx.toLocaleString('es-MX', {
                        minimumFractionDigits: 2,
                      })}
                    </Text>
                  </HStack> */}
                  <Divider />
                  <HStack justify="space-between">
                    <Text fontSize="xl" fontWeight="bold" color="#5b182b">
                      Total a Pagar:
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="#bb8c52">
                      ${calculoPago.resultado.total.toLocaleString('es-MX', {
                        minimumFractionDigits: 2,
                      })}
                    </Text>
                  </HStack>
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            bg="#bb8c52"
            color="white"
            _hover={{ bg: "#a67a47" }}
            mr={3}
            onClick={generarPago}
            isDisabled={!calculoPago || isLoading || !calculoPago.ok || !calculoPago.resultado?.total}
          >
            Generar Pago
          </Button>
          <Button
            variant="ghost"
            onClick={handleClose}
            color="#5b182b"
            _hover={{ bg: "#f7fafc" }}
          >
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalGenerarPago;